/**
 * Query keys and options, in one place.
 *
 * TanStack Query over the gateway rather than over `fetch`: the cache key is the console's
 * own vocabulary, so a screen never knows whether a value came from `GET /v1/templates` or
 * from the in-memory gateway, and the two are interchangeable in a suite.
 *
 * Every key starts with a literal namespace so an invalidation can be written against a
 * prefix without matching another screen's rows by accident.
 */
import { queryOptions } from "@tanstack/react-query";

import type {
  AuthSessionView,
  WorkspaceAuthSessionView,
  AnalyticsRange,
  AuditQueryView,
  CampaignDraftView,
  ConsoleGateway,
  DoNotCallFilterView,
  HistoryQuery,
  OptimisationReviewScopeView,
  SurveyRespondentOutcomeView,
  SurveyResultFilterView,
} from "./gateway.js";

export const QUERY_KEY = {
  authSession: ["auth-session"] as const,
  // plan-014 INT (owner answer 47): the signed-in person's verified-domain offers to join an organization.
  domainJoinOffer: ["domain-join-offer"] as const,
  workspace: ["workspace"] as const,
  notification: ["notification"] as const,
  billing: ["billing"] as const,
  firstRun: ["first-run"] as const,
  agentTemplate: ["agent-template"] as const,
  languagePack: ["language-pack"] as const,
  voice: ["voice"] as const,
  tier: ["tier"] as const,
  numberAvailability: ["number-availability"] as const,
  voiceAgent: ["voice-agent"] as const,
  dashboard: ["dashboard"] as const,
  // INT 2026-10-07: the organization's concurrency entitlement (the usage summary's), for every page that names it.
  organizationConcurrency: ["organization-concurrency"] as const,
  liveSnapshot: ["live-snapshot"] as const,
  liveActivity: ["live-activity"] as const,
  pastConversation: ["past-conversation"] as const,
  creditBalance: ["credit-balance"] as const,
  ledger: ["ledger"] as const,
  paymentMethod: ["payment-method"] as const,
  autoReload: ["auto-reload"] as const,
  spendControl: ["spend-control"] as const,
  usageDay: ["usage-day"] as const,
  chargeAttempt: ["charge-attempt"] as const,
  invoice: ["invoice"] as const,
  workspacePeople: ["workspace-people"] as const,
  apiKey: ["api-key"] as const,
  webhookEndpoint: ["webhook-endpoint"] as const,
  analytics: ["voice-agent-analytics"] as const,
  agentVersion: ["agent-version"] as const,
  testSuite: ["test-suite"] as const,
  gap: ["gap"] as const,
  conversationFlag: ["conversation-flag"] as const,
  channelBinding: ["channel-binding"] as const,
  concurrency: ["concurrency"] as const,
  connection: ["connection"] as const,
  answering: ["answering"] as const,
  signalling: ["signalling"] as const,
  number: ["number"] as const,
  numberActivity: ["number", "activity"] as const,
  sipCredential: ["sip-credential"] as const,
  regionLatency: ["region-latency"] as const,
  messagingMedium: ["messaging-medium"] as const,
  numberRequest: ["number-request"] as const,
  quotaRequest: ["quota-request"] as const,
  webChatConversation: ["web-chat-conversation"] as const,
  // P14. One namespace per bounded context, so invalidating suppression after an in-call
  // refusal does not also drop the campaign board a screen is watching.
  contactList: ["contact-list"] as const,
  // Its own namespace, not under `contactList`: invalidating the lists after a save must not
  // re-run the New list panel's inspection, which re-sends the whole file (review #136).
  contactListInspection: ["contact-list-inspection"] as const,
  campaign: ["campaign"] as const,
  campaignSkip: ["campaign-skip"] as const,
  surveyResult: ["survey-result"] as const,
  conversationSurveyAnswer: ["conversation-survey-answer"] as const,
  surveyRespondent: ["survey-respondent"] as const,
  doNotCall: ["do-not-call"] as const,
  refusalPhrase: ["refusal-phrase"] as const,
  optOutPage: ["opt-out-page"] as const,
  compliance: ["compliance"] as const,
  auditEntry: ["audit-entry"] as const,
  optimisationReview: ["optimisation-review"] as const,
} as const;

/**
 * The auth_session in whichever of its three contexts it is (plan-014 A4) — what the shell, the
 * pickers and the scope guard read. `null` when nobody is signed in.
 */
export const authContextQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: QUERY_KEY.authSession,
    queryFn: () => gateway.currentAuthSession(),
    staleTime: 30_000,
  });

/** The workspace arm of a context, or `null` for every other — the one an operational page may read. */
export function workspaceAuthSessionOf(authSession: AuthSessionView | null): WorkspaceAuthSessionView | null {
  return authSession?.kind === "workspace_selected" ? authSession : null;
}

/**
 * The auth_session as an OPERATIONAL page reads it: inside a workspace, or `null`.
 *
 * The same cache entry as {@link authContextQuery} (one key, one read), narrowed by `select`. Every
 * page behind the shell is a workspace page except the Organization, Finance and My security pages,
 * and the shell never renders a workspace page outside a workspace context (it sends the person to
 * their landing instead, `shell/shell.tsx`), so a page reading this sees `null` only in the frame
 * before that redirect — the same "not signed in to a workspace" it already handles. The route
 * guard's `ensureQueryData` is given the raw context (a `select` is an observer's), so an
 * account-only or unscoped sign-in passes the "signed in at all" check.
 */
export const authSessionQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    ...authContextQuery(gateway),
    select: workspaceAuthSessionOf,
  });

export const workspaceQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: QUERY_KEY.workspace,
    queryFn: () => gateway.listWorkspaces(),
  });

export const notificationQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: QUERY_KEY.notification,
    queryFn: () => gateway.listNotifications(),
  });

export const billingQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: QUERY_KEY.billing,
    queryFn: () => gateway.getBillingState(),
  });

export const firstRunQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: QUERY_KEY.firstRun,
    queryFn: () => gateway.getFirstRunProgress(),
  });

export const agentTemplateQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: QUERY_KEY.agentTemplate,
    queryFn: () => gateway.listAgentTemplates(),
    staleTime: 300_000,
  });

/**
 * plan-015 INT-B: whether a call may be forwarded to a phone, and its facts — from `GET /v1/templates`, so the New
 * agent wizard knows before any agent exists. A minute's staleness: a switch turned on shows on the next visit.
 */
export const forwardFactQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: [...QUERY_KEY.agentTemplate, "forward-fact"] as const,
    queryFn: () => gateway.readForwardFact(),
    staleTime: 60_000,
  });

export const languagePackQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: QUERY_KEY.languagePack,
    queryFn: () => gateway.listLanguagePacks(),
    staleTime: 300_000,
  });

export const voiceQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: QUERY_KEY.voice,
    queryFn: () => gateway.listVoices(),
    staleTime: 300_000,
  });

export const tierQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: QUERY_KEY.tier,
    queryFn: () => gateway.listTiers(),
    staleTime: 300_000,
  });

export const numberAvailabilityKeyedQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: QUERY_KEY.numberAvailability,
    queryFn: () => gateway.getNumberAvailability(),
    staleTime: 60_000,
  });

export const voiceAgentListQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: QUERY_KEY.voiceAgent,
    queryFn: () => gateway.listVoiceAgents(),
  });

export const voiceAgentQuery = (gateway: ConsoleGateway, voiceAgentId: string) =>
  queryOptions({
    queryKey: [...QUERY_KEY.voiceAgent, voiceAgentId],
    queryFn: () => gateway.getVoiceAgent(voiceAgentId),
  });

export const organizationConcurrencyQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: QUERY_KEY.organizationConcurrency,
    queryFn: () => gateway.getOrganizationConcurrency(),
  });

export const dashboardQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: QUERY_KEY.dashboard,
    queryFn: () => gateway.getDashboardFigures(),
  });

export const liveSnapshotQuery = (gateway: ConsoleGateway, voiceAgentId: string) =>
  queryOptions({
    queryKey: [...QUERY_KEY.liveSnapshot, voiceAgentId],
    queryFn: () => gateway.getLiveSnapshot(voiceAgentId),
  });

/**
 * The account-wide Live activity page (2026-09-29). Read again every five seconds: the per-agent
 * board streams its changes, but one stream per agent would open as many connections as the
 * account has agents, so the account-wide list is polled instead.
 */
export const LIVE_ACTIVITY_REFETCH_MS = 5000;

export const liveActivityQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: QUERY_KEY.liveActivity,
    queryFn: () => gateway.getLiveActivity(),
    refetchInterval: LIVE_ACTIVITY_REFETCH_MS,
  });

export const pastConversationPageQuery = (gateway: ConsoleGateway, search: HistoryQuery) =>
  queryOptions({
    queryKey: [
      ...QUERY_KEY.pastConversation,
      search.voiceAgentId,
      search.range,
      search.outcome,
      search.search,
      search.channelLabel,
      search.partyOf,
      search.phoneNumberId ?? "",
    ],
    queryFn: () => gateway.listPastConversations(search),
  });

export const pastConversationQuery = (gateway: ConsoleGateway, conversationId: string) =>
  queryOptions({
    queryKey: [...QUERY_KEY.pastConversation, "one", conversationId],
    queryFn: () => gateway.getPastConversation(conversationId),
  });

// --- billing (P11-07) ------------------------------------------------------------------

export const creditBalanceQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: QUERY_KEY.creditBalance,
    queryFn: () => gateway.getCreditBalance(),
    // The live balance moves while conversations run; a stale one would show credit an
    // agent no longer has. Short, not zero: it is three reads and a price lookup.
    staleTime: 15_000,
  });

export const ledgerQuery = (gateway: ConsoleGateway, cursor: string | null) =>
  queryOptions({
    queryKey: [...QUERY_KEY.ledger, cursor ?? "first"],
    queryFn: () => gateway.listLedgerEntries(cursor),
  });

export const paymentMethodQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: QUERY_KEY.paymentMethod,
    queryFn: () => gateway.listPaymentMethods(),
  });

export const autoReloadQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: QUERY_KEY.autoReload,
    queryFn: () => gateway.getAutoReload(),
  });

export const spendControlQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: QUERY_KEY.spendControl,
    queryFn: () => gateway.getSpendControl(),
  });

export const usageDayQuery = (gateway: ConsoleGateway, fromDay: string, toDay: string) =>
  queryOptions({
    queryKey: [...QUERY_KEY.usageDay, fromDay, toDay],
    queryFn: () => gateway.listUsageDays(fromDay, toDay),
  });

export const chargeAttemptQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: QUERY_KEY.chargeAttempt,
    queryFn: () => gateway.listChargeAttempts(),
  });

export const invoiceQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: QUERY_KEY.invoice,
    queryFn: () => gateway.listInvoices(),
    staleTime: 300_000,
  });

// --- workspaces and the developer surface (P11-08) ---------------------------------------

export const workspaceDetailQuery = (gateway: ConsoleGateway, workspaceId: string) =>
  queryOptions({
    queryKey: [...QUERY_KEY.workspace, workspaceId],
    queryFn: () => gateway.getWorkspace(workspaceId),
  });

export const workspacePeopleQuery = (gateway: ConsoleGateway, workspaceId: string) =>
  queryOptions({
    queryKey: [...QUERY_KEY.workspacePeople, workspaceId],
    queryFn: () => gateway.listWorkspacePeople(workspaceId),
  });

export const apiKeyQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: QUERY_KEY.apiKey,
    queryFn: () => gateway.listApiKeys(),
  });

export const webhookEndpointQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: QUERY_KEY.webhookEndpoint,
    queryFn: () => gateway.listWebhookEndpoints(),
  });

// --- Agent · Channels, Numbers & SIP, Messaging (P11-04 / P11-05 / P11-06) ----------------

export const channelBindingQuery = (gateway: ConsoleGateway, voiceAgentId: string) =>
  queryOptions({
    queryKey: [...QUERY_KEY.channelBinding, voiceAgentId],
    queryFn: () => gateway.listVoiceAgentChannelBindings(voiceAgentId),
  });

export const concurrencyQuery = (gateway: ConsoleGateway, voiceAgentId: string) =>
  queryOptions({
    queryKey: [...QUERY_KEY.concurrency, voiceAgentId],
    queryFn: () => gateway.getVoiceAgentConcurrency(voiceAgentId),
    // The meter moves while conversations run; the connection and the schedule beside it do
    // not, which is why they are separate queries rather than one document.
    staleTime: 5_000,
  });

export const connectionQuery = (gateway: ConsoleGateway, voiceAgentId: string) =>
  queryOptions({
    queryKey: [...QUERY_KEY.connection, voiceAgentId],
    queryFn: () => gateway.getVoiceAgentConnection(voiceAgentId),
  });

export const answeringQuery = (gateway: ConsoleGateway, voiceAgentId: string) =>
  queryOptions({
    queryKey: [...QUERY_KEY.answering, voiceAgentId],
    queryFn: () => gateway.getVoiceAgentAnswering(voiceAgentId),
    // It changes when someone publishes an agent_version, which is not often.
    staleTime: 60_000,
  });

export const signallingQuery = (gateway: ConsoleGateway, voiceAgentId: string) =>
  queryOptions({
    queryKey: [...QUERY_KEY.signalling, voiceAgentId],
    queryFn: () => gateway.listVoiceAgentSignallingEvents(voiceAgentId),
  });

export const numberQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: QUERY_KEY.number,
    queryFn: () => gateway.listNumbers(),
  });

/** numbers-sip-sync: one number's figures and latest conversations — keyed under `number`, so a number change refreshes it. */
export const numberActivityQuery = (gateway: ConsoleGateway, phoneNumberId: string) =>
  queryOptions({
    queryKey: [...QUERY_KEY.numberActivity, phoneNumberId],
    queryFn: () => gateway.readNumberActivity(phoneNumberId),
  });

export const sipCredentialQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: QUERY_KEY.sipCredential,
    queryFn: () => gateway.listSipCredentials(),
  });

export const regionLatencyQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: QUERY_KEY.regionLatency,
    queryFn: () => gateway.getRegionLatency(),
    staleTime: 60_000,
  });

export const messagingMediumListQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: QUERY_KEY.messagingMedium,
    queryFn: () => gateway.listMessagingMedia(),
  });

export const messagingMediumQuery = (gateway: ConsoleGateway, mediumCode: string) =>
  queryOptions({
    queryKey: [...QUERY_KEY.messagingMedium, mediumCode],
    queryFn: () => gateway.getMessagingMedium(mediumCode),
  });

export const webChatConversationQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: QUERY_KEY.webChatConversation,
    queryFn: () => gateway.listWebChatConversations(),
  });

// --- Agent · Analytics, Versions and Review (P11-01/02/03) --------------------------------

export const voiceAgentAnalyticsQuery = (
  gateway: ConsoleGateway,
  voiceAgentId: string,
  range: AnalyticsRange,
) =>
  queryOptions({
    queryKey: [...QUERY_KEY.analytics, voiceAgentId, range],
    queryFn: () => gateway.getVoiceAgentAnalytics(voiceAgentId, range),
    // Eight grouped aggregates over the account's conversation history is an `expensive`
    // route on the server side. A minute of staleness is the right trade for a tab a person
    // flips back to; the live figures are the Live tab's job, not this one's.
    staleTime: 60_000,
  });

export const agentVersionListQuery = (gateway: ConsoleGateway, voiceAgentId: string) =>
  queryOptions({
    queryKey: [...QUERY_KEY.agentVersion, voiceAgentId],
    queryFn: () => gateway.listAgentVersions(voiceAgentId),
  });

export const testSuiteQuery = (gateway: ConsoleGateway, voiceAgentId: string) =>
  queryOptions({
    queryKey: [...QUERY_KEY.testSuite, voiceAgentId],
    queryFn: () => gateway.listVoiceAgentTestSuites(voiceAgentId),
  });

export const gapQuery = (gateway: ConsoleGateway, voiceAgentId: string) =>
  queryOptions({
    queryKey: [...QUERY_KEY.gap, voiceAgentId],
    queryFn: () => gateway.listGaps(voiceAgentId),
  });

export const conversationFlagQuery = (gateway: ConsoleGateway, voiceAgentId: string) =>
  queryOptions({
    queryKey: [...QUERY_KEY.conversationFlag, voiceAgentId],
    queryFn: () => gateway.listConversationFlags(voiceAgentId),
  });

// --- Contact lists, campaigns and survey results (P14-01 / P14-02 / P14-03) ---------------

export const contactListQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: QUERY_KEY.contactList,
    queryFn: () => gateway.listContactLists(),
  });

export const campaignListQuery = (gateway: ConsoleGateway, voiceAgentId: string) =>
  queryOptions({
    queryKey: [...QUERY_KEY.campaign, voiceAgentId],
    queryFn: () => gateway.listCampaigns(voiceAgentId),
  });

/** The questions a campaign for this agent asks: its own questionnaire, read from its live version (2026-10-04). */
export const voiceAgentQuestionnaireQuery = (gateway: ConsoleGateway, voiceAgentId: string) =>
  queryOptions({
    queryKey: [...QUERY_KEY.campaign, "questionnaire", voiceAgentId],
    queryFn: () => gateway.getVoiceAgentQuestionnaire(voiceAgentId),
    // It changes only when the agent is republished.
    staleTime: 60_000,
  });

/** What New campaign opens with for this agent — the sandbox's worked example, or `null`. */
export const campaignSuggestionQuery = (gateway: ConsoleGateway, voiceAgentId: string) =>
  queryOptions({
    queryKey: [...QUERY_KEY.campaign, "suggestion", voiceAgentId],
    queryFn: () => gateway.suggestCampaignDraft(voiceAgentId),
    staleTime: Infinity,
  });

export const campaignQuery = (gateway: ConsoleGateway, campaignId: string) =>
  queryOptions({
    queryKey: [...QUERY_KEY.campaign, "one", campaignId],
    queryFn: () => gateway.getCampaign(campaignId),
    // A running campaign's counters move while somebody watches them, and the progress bar
    // is the whole reason this screen is open. Short, not zero: it is several aggregates.
    staleTime: 15_000,
  });

/**
 * The New campaign estimate. The estimate is a POST, but it is PURE — it writes nothing (design
 * §15.6.5) — so it is read as a query keyed by exactly the fields the API reads, and changing any
 * of them asks again. Under the `campaign` namespace, so an invalidation of campaigns also drops a
 * stale estimate.
 */
export const campaignEstimateQuery = (gateway: ConsoleGateway, draft: CampaignDraftView) =>
  queryOptions({
    queryKey: [
      ...QUERY_KEY.campaign,
      "estimate",
      draft.voiceAgentId,
      draft.contactListId,
      // 2026-10-30: every calling range, in order.
      draft.callingHours.map((one) => `${String(one.dayOfWeek)}@${one.fromLocalTime}-${one.toLocalTime}`).join(","),
      draft.simultaneousCall,
      // plan-017: the caller-ID number picks the route, so the outbound rate the band is priced at.
      draft.callerIdPhoneNumberId ?? "",
    ],
    queryFn: () => gateway.estimateCampaign(draft),
    // The band moves only when one of the keyed fields does.
    staleTime: 60_000,
  });

export const campaignSkipQuery = (
  gateway: ConsoleGateway,
  campaignId: string,
  cursor: string | null,
) =>
  queryOptions({
    queryKey: [...QUERY_KEY.campaignSkip, campaignId, cursor ?? "first"],
    queryFn: () => gateway.listCampaignSkips(campaignId, cursor),
  });

/**
 * A survey VoiceAgent's results under one filter (AMENDED 2026-10-03: read from `survey_answer`). The
 * filter is part of the key, so switching a chip is a new read and switching back is a cached one.
 */
export const surveyResultQuery = (gateway: ConsoleGateway, voiceAgentId: string, filter: SurveyResultFilterView) =>
  queryOptions({
    queryKey: [...QUERY_KEY.surveyResult, voiceAgentId, filter],
    queryFn: () => gateway.getSurveyResult(voiceAgentId, filter),
    // Grouped aggregates over every answer in the window; they move as conversations finish.
    staleTime: 60_000,
  });

export const surveyRespondentQuery = (
  gateway: ConsoleGateway,
  voiceAgentId: string,
  filter: SurveyResultFilterView,
  outcome: SurveyRespondentOutcomeView | "all",
  cursor: string | null,
) =>
  queryOptions({
    queryKey: [...QUERY_KEY.surveyRespondent, voiceAgentId, filter, outcome, cursor ?? "first"],
    queryFn: () => gateway.listSurveyRespondents(voiceAgentId, filter, outcome, cursor),
  });

/** One conversation's survey answers, for History's detail. */
export const conversationSurveyAnswerQuery = (gateway: ConsoleGateway, conversationId: string) =>
  queryOptions({
    queryKey: [...QUERY_KEY.conversationSurveyAnswer, conversationId],
    queryFn: () => gateway.getConversationSurveyAnswer(conversationId),
  });

// --- Do not call and the opt-out page (P14-04) --------------------------------------------

export const doNotCallQuery = (
  gateway: ConsoleGateway,
  filter: DoNotCallFilterView,
  search: string,
  cursor: string | null,
) =>
  queryOptions({
    queryKey: [...QUERY_KEY.doNotCall, filter, search, cursor ?? "first"],
    queryFn: () => gateway.listDoNotCallEntries(filter, search, cursor),
  });

export const refusalPhraseQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: QUERY_KEY.refusalPhrase,
    queryFn: () => gateway.getRefusalPhrases(),
    // It changes when a language pack ships, which is not a per-session event.
    staleTime: 300_000,
  });

export const optOutPageQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: QUERY_KEY.optOutPage,
    queryFn: () => gateway.getOptOutPage(),
    staleTime: 300_000,
  });

// --- Compliance and Audit log (P14-05 / P14-06) -------------------------------------------

export const complianceQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: QUERY_KEY.compliance,
    queryFn: () => gateway.getCompliance(),
  });

export const auditEntryQuery = (gateway: ConsoleGateway, query: AuditQueryView) =>
  queryOptions({
    queryKey: [...QUERY_KEY.auditEntry, query.eventClass ?? "everything", query.cursor ?? "first"],
    queryFn: () => gateway.listAuditEntries(query),
  });

// --- Optimisation review (P14-07) ----------------------------------------------------------

export const optimisationReviewQuoteQuery = (
  gateway: ConsoleGateway,
  voiceAgentId: string,
  scope: OptimisationReviewScopeView,
) =>
  queryOptions({
    queryKey: [...QUERY_KEY.optimisationReview, "quote", voiceAgentId, scope],
    queryFn: () => gateway.getOptimisationReviewQuote(voiceAgentId, scope),
    // The price is a price-book read and the counts are a scan; both move slowly, and a
    // stale quote beside a "spend €18.40" button is the one thing this must not be.
    staleTime: 30_000,
  });

export const optimisationReviewQuery = (gateway: ConsoleGateway, optimisationReviewId: string) =>
  queryOptions({
    queryKey: [...QUERY_KEY.optimisationReview, optimisationReviewId],
    queryFn: () => gateway.getOptimisationReview(optimisationReviewId),
  });
